/* eslint-disable id-length */
import { ChartData, TooltipModel } from "chart.js";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "app/components/shared/HoverCard";

import LineChart from "../LineChart";
import {
  ThemeNegativeTrend,
  ThemeDefaultTrend,
  colorsForIndex,
} from "../Theme";
import { TimePeriod } from "../Renderer";
import {
  Card,
  CardSection,
  CardChart,
  CardLabel,
  CardDelta,
  CardStat,
} from "../Card";
import SparkLineChart, {
  withDashedLine,
  withGradientFillLine,
} from "../SparkLineChart";
import {
  calculateMax,
  calculateMin,
  calculateSum,
  formatTooltipDateString,
  getLatestValue,
  getTimePeriodString,
} from "./utils";
import { useEffect, useMemo } from "react";
import { useChart } from "app/components/shared/Chart/chart";
import { ChartLegend } from "../ChartLegend";
import { min, max } from "lodash";

interface Props {
  datasets: {
    current: { x: number; y: number }[];
    previous: { x: number; y: number }[];
  };
}

export function QueueJobsWaitingSpark({ datasets }: Props) {
  const previousValue = getLatestValue(datasets.previous, "y");
  const latestValue = getLatestValue(datasets.current, "y");

  const delta = latestValue - previousValue;
  const theme = delta > 0 ? ThemeNegativeTrend : ThemeDefaultTrend;
  const formattedDelta = Math.abs(delta);

  return (
    <Card>
      <CardSection>
        <CardLabel>Queued jobs waiting</CardLabel>
        <CardDelta
          formattedValue={formattedDelta}
          value={delta}
          color={theme.color}
        />
      </CardSection>

      <CardSection>
        <CardStat
          label="Waiting"
          value={latestValue}
          size="lg"
          layout="stacked"
        />

        <CardSection className="flex-col">
          <CardStat label="Min" value={calculateMin(datasets.current)} />
          <CardStat label="Max" value={calculateMax(datasets.current)} />
        </CardSection>
      </CardSection>

      <CardChart>
        <SparkLineChart
          data={{
            labels: datasets.current.map(({ x }) => x),
            datasets: [
              withGradientFillLine(
                datasets.current.map(({ y }) => y),
                theme,
              ),
              withDashedLine(datasets.previous.map(({ y }) => y)),
            ],
          }}
        />
      </CardChart>
    </Card>
  );
}

enum Scope {
  Queue = "queue",
  Cluster = "cluster",
  Organization = "organization",
}

interface DetailProps {
  scope: Scope;
  timePeriod: TimePeriod;
  datasets: {
    label: string;
    data: { x: number; y: number }[];
  }[];
}

const lineOptionsForScope = (scope: Scope) => {
  switch (scope) {
    case Scope.Organization:
    case Scope.Cluster:
      return {
        fill: true,
        borderWidth: 1.75,
      };
    default:
      return {
        borderWidth: 1.75,
      };
  }
};

const chartOptionsForScope = (scope: Scope) => {
  switch (scope) {
    case Scope.Organization:
    case Scope.Cluster:
      return {
        scales: {
          y: {
            stacked: true,
          },
        },
      };
    default:
      return {};
  }
};

export function QueueJobsWaitingDetail({
  datasets,
  timePeriod,
  scope,
}: DetailProps) {
  const totalMin = Math.min(
    Infinity,
    ...datasets.map((item) => calculateMin(item.data)),
  );
  const totalMax = Math.max(
    -Infinity,
    ...datasets.map((item) => calculateMax(item.data)),
  );

  const data: ChartData = useMemo(
    () => ({
      labels: datasets[0].data.map((item) => item.x),
      datasets: datasets.map((dataset, index) => {
        return {
          label: dataset.label,
          data: dataset.data.map((item) => item.y),
          ...colorsForIndex(index),
          ...lineOptionsForScope(scope),
        };
      }),
    }),
    [datasets],
  );

  const { chartRef, updateData } = useChart();

  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex flex-col grow gap-3 min-w-0">
        <CardSection>
          <CardLabel description={getTimePeriodString(timePeriod)}>
            Queue jobs waiting
          </CardLabel>

          <CardSection className="flex-col">
            <CardStat value={totalMin} label="Min" />
            <CardStat value={totalMax} label="Max" />
          </CardSection>
        </CardSection>

        <CardChart>
          <LineChart
            data={data}
            ref={chartRef}
            options={chartOptionsForScope(scope)}
            tooltip={Tooltip}
          />
        </CardChart>
      </div>

      <ChartLegend
        datasets={data.datasets}
        chartRef={chartRef}
        columns={{
          Waiting: (_, index) => (
            <span>{getLatestValue(datasets[index].data, "y")}</span>
          ),
          Min: (dataset) => <span>{min(dataset.data)}</span>,
          Max: (dataset) => <span>{max(dataset.data)}</span>,
        }}
      />
    </Card>
  );
}

const Tooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const sumJobsWaiting = calculateSum(
    tooltip.dataPoints.map(({ parsed }) => parsed) as {
      x: number;
      y: number;
    }[],
  );
  const dateString = formatTooltipDateString(tooltip.dataPoints[0].parsed.x);

  return (
    <HoverCard open={true}>
      <HoverCardTrigger />
      <HoverCardContent side="right" className="w-auto px-2 py-2.5">
        <div className="flex flex-col gap-1.5">
          <div className="font-semibold text-xs">{dateString}</div>

          {tooltip.dataPoints.map(({ dataset, parsed }, index) => {
            return (
              <div
                key={index}
                className="font-semibold text-xs"
                style={{ color: dataset.borderColor as string }}
              >
                {dataset.label}:{" "}
                <span className="font-medium text-charcoal-300">
                  {parsed.y} jobs
                </span>
              </div>
            );
          })}

          <div className="font-semibold text-xs">
            Total:{" "}
            <span className="font-medium text-charcoal-300">
              {sumJobsWaiting} jobs
            </span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
